import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { BRAND } from "@/lib/site";

const SITE_URL = process.env.REACT_APP_SITE_URL || "https://www.concreteinsightuk.co.uk";
const OG_IMAGE = `${SITE_URL}/og-default.jpg`;

/**
 * SEO, per-page metadata (title, description, OG, Twitter, canonical, optional JSON-LD)
 * Usage:
 * <SEO title="Services" description="..." />
 */
export default function SEO({
  title,
  description,
  path,
  image = OG_IMAGE,
  jsonLd,
  noindex = false,
}) {
  const location = useLocation();
  const canonicalPath = path ?? location.pathname;
  const fullTitle = title
    ? `${title} · ${BRAND.name}`
    : `Concrete Repair & Reinforcement Corrosion Specialists · ${BRAND.name}`;
  const canonical = `${SITE_URL}${canonicalPath === "/" ? "" : canonicalPath}`;
  const desc = description
    ? description
    : "UK specialist consultancy for reinforced concrete affected by chemical attack, reinforcement corrosion and fire damage. Investigation, diagnosis and EN 1504 repair.";

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_GB" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

/** LocalBusiness structured-data, used on Home page */
export const buildLocalBusinessJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}#business`,
  name: BRAND.name,
  url: SITE_URL,
  telephone: BRAND.phoneDial,
  email: BRAND.email,
  image: OG_IMAGE,
  logo: BRAND.logoUrl,
  description:
    "Specialist UK consultancy diagnosing and repairing reinforced concrete affected by chemical attack (chlorides, carbonation, sulphates, ASR), reinforcement corrosion and fire damage.",
  address: {
    "@type": "PostalAddress",
    streetAddress: BRAND.address.line1,
    addressLocality: "Chichester",
    addressRegion: "West Sussex",
    postalCode: BRAND.address.postcode,
    addressCountry: "GB",
  },
  areaServed: {
    "@type": "Country",
    name: "United Kingdom",
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    },
  ],
});

/** Service structured-data, used on Services page */
export const buildServicesJsonLd = (services) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Concrete Repair & Reinforcement Corrosion Diagnosis",
  provider: {
    "@type": "LocalBusiness",
    name: BRAND.name,
    url: SITE_URL,
  },
  areaServed: { "@type": "Country", name: "United Kingdom" },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Concrete Repair Services",
    itemListElement: services.map((s, i) => ({
      "@type": "Offer",
      position: i + 1,
      itemOffered: {
        "@type": "Service",
        name: s.title,
        description: s.body,
      },
    })),
  },
});

/** Breadcrumb structured-data */
export const buildBreadcrumbJsonLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE_URL}${c.path}`,
  })),
});
